import { corridorQuotePushesQuery } from "@common/api/corridor-relay"
import { currentUserQuery } from "@common/api/users"
import type { CorridorQuotePushPublic } from "@common/client"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@common/components/ui/dialog"
import { formatShortDate } from "@common/lib/date-utils"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { CorridorPushes } from "./-components/corridor-pushes"
import { CorridorSendButton } from "./-components/corridor-send-button"

export const Route = createFileRoute("/_layout/quotes_/$quoteId/corridor")({
  beforeLoad: async ({ context: { queryClient }, params }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "quote_push:create")) {
      throw redirect({ to: "/quotes/$quoteId", params })
    }
  },
  loader: ({ context: { queryClient }, params: { quoteId } }) =>
    queryClient.ensureQueryData(
      corridorQuotePushesQuery({ quote_id: quoteId }),
    ),
  component: SendToCorridorDialog,
})

function lastSent(
  pushes: CorridorQuotePushPublic[],
): CorridorQuotePushPublic | undefined {
  return pushes.find((push) => push.status === "succeeded")
}

function SendToCorridorDialog(): React.ReactElement {
  const { quoteId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const { data: pushes } = useSuspenseQuery(
    corridorQuotePushesQuery({ quote_id: quoteId }),
  )
  const sent = lastSent(pushes.data)

  const close = () =>
    navigate({
      to: "/quotes/$quoteId",
      params: { quoteId },
      search: (previous) => previous,
    })

  const queued = (push: CorridorQuotePushPublic) =>
    showToast(
      "Queued for Corridor",
      `Position ${push.queue_position ?? 1} in the Corridor queue.`,
      "success",
    )

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send to Corridor</DialogTitle>
          <DialogDescription>
            {sent
              ? `Sent as ${sent.corridor_number} on ${formatShortDate(sent.created_at)}. Sending again creates a new Corridor quote.`
              : "Creates this quote as a new Work Order Quote in Corridor, under the quote's department. Excluded and rejected tasks are left out."}
          </DialogDescription>
        </DialogHeader>
        {pushes.relay_configured ? (
          <CorridorSendButton
            quoteId={quoteId}
            sentBefore={Boolean(sent)}
            onSend={queued}
          />
        ) : (
          <p className="text-small-regular text-muted-foreground">
            Set up the Corridor relay in Settings › Integrations › ERP first.
          </p>
        )}
        {pushes.data.length > 0 && <CorridorPushes pushes={pushes.data} />}
      </DialogContent>
    </Dialog>
  )
}
